import React, { ErrorInfo, ReactNode } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  AlertTriangle, 
  RefreshCw, 
  Copy, 
  Bug,
  Info,
  Code,
  FileText
} from 'lucide-react';
import { devUtils } from '@/lib/devUtils';
import { captureError } from '@/lib/sentry';

interface ErrorBoundaryDevProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showInProduction?: boolean;
}

interface ErrorBoundaryDevState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
}

export class ErrorBoundaryDev extends React.Component<
  ErrorBoundaryDevProps,
  ErrorBoundaryDevState
> {
  constructor(props: ErrorBoundaryDevProps) {
    super(props);
    this.state = { 
      hasError: false, 
      errorId: '' 
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryDevState> {
    return { 
      hasError: true, 
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const isDev = process.env.NODE_ENV === 'development';
    
    if (isDev) {
      devUtils.log.error('ErrorBoundary caught an error', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        errorId: this.state.errorId
      });
    }

    this.setState({ errorInfo });
    this.props.onError?.(error, errorInfo);

    // Report to Sentry
    captureError(error, {
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
    });
    this.reportError(error, errorInfo);
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    // In production, you would send this to your error monitoring service
    const errorReport = {
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: 'anonymous', // Replace with actual user ID if available
      buildVersion: '1.0.0' // Replace with actual build version
    };

    if (process.env.NODE_ENV === 'development') {
      console.group('🚨 Error Report');
      console.table(errorReport);
      console.groupEnd();
    }

    // Example: Send to error monitoring service
    // errorMonitoringService.report(errorReport);
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: undefined, 
      errorInfo: undefined,
      errorId: ''
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  copyErrorDetails = () => {
    const errorDetails = {
      errorId: this.state.errorId,
      message: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    navigator.clipboard.writeText(JSON.stringify(errorDetails, null, 2));
    devUtils.log.info('Error details copied to clipboard');
  };

  copyStackTrace = () => {
    const stackTrace = this.state.error?.stack || 'No stack trace available';
    navigator.clipboard.writeText(stackTrace);
    devUtils.log.info('Stack trace copied to clipboard');
  };

  render() {
    const isDev = process.env.NODE_ENV === 'development';
    const showDevError = isDev || this.props.showInProduction;

    if (this.state.hasError) {
      if (this.props.fallback && !showDevError) {
        return this.props.fallback;
      }

      // Production error fallback
      if (!showDevError) {
        return (
          <div className="flex items-center justify-center min-h-[400px] p-6">
            <Alert variant="destructive" className="max-w-md">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Something went wrong</AlertTitle>
              <AlertDescription className="mt-2">
                We're sorry, but something unexpected happened. Please try refreshing the page.
                <div className="mt-4 flex gap-2">
                  <Button onClick={this.handleReload} size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Page
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          </div>
        );
      }

      // Development error display
      return (
        <div className="p-6 min-h-[400px] bg-red-50 dark:bg-red-950/10">
          <Card className="max-w-6xl mx-auto border-red-200 dark:border-red-800">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
                  <Bug className="h-5 w-5" />
                  Application Error
                  <Badge variant="destructive" className="ml-2">
                    {this.state.errorId}
                  </Badge>
                </CardTitle>
                <div className="flex gap-2">
                  <Button onClick={this.copyErrorDetails} size="sm" variant="outline">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Details
                  </Button>
                  <Button onClick={this.handleReset} size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Try Again
                  </Button>
                  <Button onClick={this.handleReload} size="sm" variant="outline">
                    Reload Page
                  </Button>
                </div>
              </div>
            </CardHeader>

            <CardContent>
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">
                    <Info className="w-4 h-4 mr-1" />
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="stack">
                    <Code className="w-4 h-4 mr-1" />
                    Stack Trace
                  </TabsTrigger>
                  <TabsTrigger value="component">
                    <FileText className="w-4 h-4 mr-1" />
                    Component Stack
                  </TabsTrigger>
                  <TabsTrigger value="context">
                    <Bug className="w-4 h-4 mr-1" />
                    Context
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Error Message</AlertTitle>
                    <AlertDescription className="font-mono text-sm mt-2">
                      {this.state.error?.message || 'Unknown error occurred'}
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Error Type</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Badge variant="secondary">
                          {this.state.error?.name || 'Error'}
                        </Badge>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Timestamp</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <span className="text-sm font-mono">
                          {new Date().toLocaleString()}
                        </span>
                      </CardContent>
                    </Card>
                  </div>
                </TabsContent>

                <TabsContent value="stack">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">JavaScript Stack Trace</h3>
                      <Button onClick={this.copyStackTrace} size="sm" variant="outline">
                        <Copy className="h-3 w-3 mr-1" />
                        Copy
                      </Button>
                    </div>
                    <ScrollArea className="h-96 border rounded-md p-4 bg-muted">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {this.state.error?.stack || 'No stack trace available'}
                      </pre>
                    </ScrollArea>
                  </div>
                </TabsContent>

                <TabsContent value="component">
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">React Component Stack</h3>
                    <ScrollArea className="h-96 border rounded-md p-4 bg-muted">
                      <pre className="text-xs font-mono whitespace-pre-wrap">
                        {this.state.errorInfo?.componentStack || 'No component stack available'}
                      </pre>
                    </ScrollArea>
                  </div>
                </TabsContent>

                <TabsContent value="context">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">Browser Info</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="text-xs">
                            <span className="font-medium">User Agent:</span>
                            <div className="font-mono bg-muted p-1 rounded mt-1 break-all">
                              {navigator.userAgent}
                            </div>
                          </div>
                          <div className="text-xs">
                            <span className="font-medium">URL:</span>
                            <div className="font-mono bg-muted p-1 rounded mt-1 break-all">
                              {window.location.href}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader>
                          <CardTitle className="text-sm">Environment</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="flex justify-between text-xs">
                            <span>Mode:</span>
                            <Badge variant="secondary">
                              {process.env.NODE_ENV}
                            </Badge>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span>React Version:</span>
                            <Badge variant="secondary">
                              {React.version}
                            </Badge>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span>Viewport:</span>
                            <Badge variant="secondary">
                              {window.innerWidth}x{window.innerHeight}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertTitle>Debug Tips</AlertTitle>
                      <AlertDescription>
                        <ul className="list-disc list-inside space-y-1 text-sm mt-2">
                          <li>Check the React DevTools for component state</li>
                          <li>Inspect the Network tab for failed requests</li>
                          <li>Look for console errors before this crash</li>
                          <li>Copy the error details and share with the development team</li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}